import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import * as fromRoot from '../store/app.reducers';
import {first, map, mergeMap} from 'rxjs/operators';
import {Observable, of, zip} from 'rxjs';
import {State} from '../store/user/user.reducers';
import {APP_CONFIG} from '../misc/inject-tokens';
import {AppConfig} from '../model/app-config.model';

/**
 * This service handles feature-settings http calls to the respective REST endpoints
 */
@Injectable()
export class SystemSettingsService {

  constructor(private http: HttpClient, private store: Store<fromRoot.AppState>,
              @Inject(APP_CONFIG) private config: AppConfig) {
  }

  /**
   * Checks if the system feature is activated for the given channel for the currently logged-in dealer and return
   * an Observable holding a boolean.
   *
   * @param feature The feature to check.
   * @param channel The channel to check for.
   */
  isSystemFeatureActivatedForChannel(feature: SystemFeature, channel: DistributionChannel): Observable<boolean> {
    return this.store.select(fromRoot.getUserInfo)
      .pipe(first(),
        map(userInfo => {
          try {
            return userInfo.enabledFeaturesPerChannelsAndDealers
              .find(features => features.channel === channel).companyFeatures
              .find(companyFeatures => companyFeatures.companyNumber === userInfo.dealers
                .find(dealer => dealer.id === userInfo.currentDealerId).companyNumber).features
              .includes(feature);
          } catch (e) {
            console.debug(e);
            return false;
          }
        }));
  }

  isSystemFeatureActivatedForChannelOfUserInfo(feature: SystemFeature, channel: DistributionChannel,
    userInfo: UserInfoDto): boolean {
    return userInfo.enabledFeaturesPerChannelsAndDealers
      .find(features => features.channel === channel).companyFeatures
      .find(companyFeatures => companyFeatures.companyNumber === userInfo.dealers
        .find(dealer => dealer.id === userInfo.currentDealerId).companyNumber).features
      .includes(feature);
  }

  /**
   * Checks if the system feature is activated for any channel, for which the currently logged-in user (using the
   * current dealer) has a role for.
   * @param feature The system feature which to check for.
   */
  isSystemFeatureActivatedForAnyChannel(feature: SystemFeature): Observable<boolean> {
    return this.store.select(fromRoot.getUserState)
      .pipe(first(),
        mergeMap(userState => {
          const allUcResult = userState.hasAnyRoleForAllUc ?
            this.isSystemFeatureActivatedForChannel(feature, 'ALL_UC') : of(false);
          const pbResult = userState.hasAnyRoleForPb ?
            this.isSystemFeatureActivatedForChannel(feature, 'PB') : of(false);
          const piaResult = userState.hasAnyRoleForPia ?
            this.isSystemFeatureActivatedForChannel(feature, 'PIA') : of(false);
          const dinbilResult = userState.hasAnyRoleForDinBil ?
            this.isSystemFeatureActivatedForChannel(feature, 'DIN_BIL') : of(false);
          const vgrxResult = userState.hasAnyRoleForVgrx ?
            this.isSystemFeatureActivatedForChannel(feature, 'VGRX') : of(false);
          return zip(allUcResult, pbResult, piaResult, dinbilResult, vgrxResult).pipe(
            map(([allUc, pb, pia, dinbil, vgrx]) => {
              return (allUc || pb || pia || dinbil || vgrx);
            }),
          );
        }),
      );
  }

  isSystemFeatureActivatedForAnyChannelOfState(feature: SystemFeature, userState: State): boolean {
    const allUcResult = userState.hasAnyRoleForAllUc ?
      this.isSystemFeatureActivatedForChannelOfUserInfo(feature, 'ALL_UC', userState.userInfo) : false;
    const dinBilResult = userState.hasAnyRoleForDinBil ?
      this.isSystemFeatureActivatedForChannelOfUserInfo(feature, 'DIN_BIL', userState.userInfo) : false;
    const pbResult = userState.hasAnyRoleForPb ?
      this.isSystemFeatureActivatedForChannelOfUserInfo(feature, 'PB', userState.userInfo) : false;
    const piaResult = userState.hasAnyRoleForPia ?
      this.isSystemFeatureActivatedForChannelOfUserInfo(feature, 'PIA', userState.userInfo) : false;
    const vgrxResult = userState.hasAnyRoleForVgrx ?
      this.isSystemFeatureActivatedForChannelOfUserInfo(feature, 'VGRX', userState.userInfo) : false;

    return allUcResult || pbResult || piaResult || dinBilResult || vgrxResult;
  }

  /**
   * Returns the value of a system feature setting of a given key for the given channel, or undefined if the value could not be found
   * @param featureKey The feature key for which to get the value.
   * @param channel The channel to get the setting for.
   */
  getSystemFeatureSettingForChannel(featureKey: string, channel: DistributionChannel): Observable<string> {
    return this.store.select(fromRoot.getUserInfo)
      .pipe(
        map(userInfo => {
          try {
            const featureSetting = userInfo.featureSettingsPerChannelsAndDealers
              .find(settings => settings.channel === channel).companyFeatureSettings
              .find(companyFeatureSettings => companyFeatureSettings.companyNumber === userInfo.dealers
                .find(dealer => dealer.id === userInfo.currentDealerId).companyNumber).featureSettings
              .find(setting => setting.key === featureKey);
            if (featureSetting) {
              return featureSetting.value;
            }
            return undefined;
          } catch (e) {
            console.debug(e, featureKey, channel);
            return undefined;
          }
        }));
  }

  /**
   * Invalidates the feature settings cache.
   */
  invalidateSettingsCache(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.request('DELETE', this.config.salesApiUrl + '/feature-settings/cache', httpOptions);
  }

  getHiddenVehicleData(channel: DistributionChannel): Observable<string[]> {
    return this.getSystemFeatureSettingForChannel('VEHICLE_MAINTENANCE.hidden_vehicle_data', channel)
      .pipe(first(), map(settings => {
        const data = settings ? JSON.parse(settings) : undefined;
        return data ? (<HiddenData>data)['hidden-data'] : [];
      }));
  }

  getTemporaryFeatureToggles(): Observable<FeatureToggleDto[]> {
    return this.http.get<FeatureToggleDto[]>(this.config.salesApiUrl + '/feature-settings/features/temporary');
  }

  updateTemporaryFeatureToggleStatus(updateDto: FeatureToggleDto): Observable<any> {
    return this.http.put(this.config.salesApiUrl + '/feature-settings/features/temporary', updateDto);
  }
}
